/**
 * One clock for the convention.
 *
 * The software was time-aware in four separate ways and none of them could talk to the
 * others. The schedule compared wall-clock strings, the room recorder counted minutes
 * since midnight, the progress bar under the running block counted its own minutes since
 * midnight through its own helper, and the lock-screen card worked in real instants. Each
 * was right on its own. The cost showed up the fifth time somebody needed the time: they
 * could not reuse any of the four, so they wrote a fifth.
 *
 * This is built on instants, and that is the one decision everything else follows from.
 * Minutes since midnight carries no date, so day one and day two of a two-day convention
 * hold the same numbers and a room running entries back to back across midnight breaks
 * outright. Wall-clock strings carry the date but not the zone, so a teacher whose phone
 * is on Central time reads the wrong hour all day. An instant carries both.
 *
 * The clock answers one question about one window: where the moment sits in it, how far
 * into it, and how long until the next boundary. Above it, callers ask and stop doing
 * arithmetic.
 */

/**
 * A window as the printed programme states it: a date, a start and an end, in the
 * convention's own time zone. Not a moment until somebody says where.
 */
export interface ConventionWindow {
  /** "2026-09-17". */
  date: string;
  /** "11:30". */
  startTime: string;
  /** "12:15". */
  endTime: string;
}

/**
 * Where a moment sits against one window.
 *
 * `imminent` is the grace before the start — near enough that a recorder should already be
 * rolling and a card should already be urgent, but the session has not begun. Without a
 * name for it, every caller invents its own five minutes.
 */
export type ConventionWindowState = "ahead" | "imminent" | "running" | "finished";

/**
 * A window already stated as two moments, for anything that stores it that way rather
 * than as a date and a printed time.
 */
export interface InstantWindow {
  /** ISO instant. */
  startsAt: string;
  /** ISO instant. */
  endsAt: string;
}

export interface ConventionWindowReading {
  state: ConventionWindowState;
  /** The moment the window starts, as an ISO instant. */
  startsAt: string;
  /** The moment the window ends, as an ISO instant. */
  endsAt: string;
  /** Whole minutes since the window started. Zero before it does. */
  minutesElapsed: number;
  /** Whole minutes until the window starts. Zero once it has. */
  minutesUntilStart: number;
  /** Whole minutes until the window ends. Zero once it has. */
  minutesUntilEnd: number;
  /**
   * How far through, from 0 to 1, or null for a window with no length. Smooth rather than
   * stepped, so a bar drawn from it moves rather than jumps once a minute.
   */
  fraction: number | null;
}

/**
 * A recording may begin a little early and run a little late.
 *
 * A presenter who starts talking before the hour, or answers questions past the end, is
 * the ordinary case rather than an exception. Five minutes either side means the recorder
 * catches both without a volunteer having to watch a clock.
 *
 * It lives here rather than beside the recorder because the session page needs the same
 * five minutes and could not reach it there. A second five written somewhere else is two
 * rules that agree until one of them is edited.
 */
export const ROOM_RECORDER_GRACE_MINUTES = 5;

/**
 * Where the clock has reached in one window.
 *
 * The grace widens the window at both ends: a moment inside the grace before the start
 * reads `imminent`, and one inside the grace after the end still reads `running`. With no
 * grace the window is exactly what the programme says.
 *
 * Throws if the time zone is not one the runtime knows, which is what `Intl` does with it
 * anyway. Callers that would rather show nothing than something wrong should catch.
 */
export function readConventionWindow(
  period: ConventionWindow,
  now: Date,
  timeZone: string,
  graceMinutes = 0,
): ConventionWindowReading {
  return readInstantWindow({
    startsAt: instantOf(period.date, period.startTime, timeZone),
    endsAt: instantOf(period.date, period.endTime, timeZone),
  }, now, graceMinutes);
}

/**
 * A window that is already two moments.
 *
 * Fine Arts stores a performance this way — an instant to start and an instant to end —
 * because a festival room runs entries back to back and a schedule that shifts by nine
 * minutes shifts every one of them. A printed programme states a date and a wall-clock
 * time; a running festival states moments. Both are the same window, so both are read by
 * the same rule and neither gets its own idea of when a thing is over.
 */
export function readInstantWindow(
  period: InstantWindow,
  now: Date,
  graceMinutes = 0,
): ConventionWindowReading {
  const start = Date.parse(period.startsAt);
  const end = Date.parse(period.endsAt);
  const at = now.getTime();
  const grace = graceMinutes * 60_000;

  const length = end - start;
  const state: ConventionWindowState = at > end + grace
    ? "finished"
    : at >= start
      ? "running"
      : at >= start - grace
        ? "imminent"
        : "ahead";

  return {
    state,
    startsAt: period.startsAt,
    endsAt: period.endsAt,
    minutesElapsed: wholeMinutes(at - start),
    minutesUntilStart: wholeMinutes(start - at),
    minutesUntilEnd: wholeMinutes(end - at),
    fraction: length > 0 ? Math.min(1, Math.max(0, (at - start) / length)) : null,
  };
}

/**
 * Which of these windows is running, and which is next.
 *
 * The same question the convention schedule asks, for anything holding its windows as
 * moments. A festival room asks it once a second on a screen at the door, and asked it in
 * two places with two copies of the same comparison before this existed.
 *
 * The end is exclusive, as everywhere else on this clock: at the moment one entry ends and
 * the next begins, the room is on the next one.
 */
export function positionInWindows<T extends InstantWindow>(
  windows: readonly T[],
  now: Date,
): { current: T | null; next: T | null } {
  const at = now.getTime();
  const ordered = [...windows].sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt));
  const current = ordered.find((window) => Date.parse(window.startsAt) <= at && at < Date.parse(window.endsAt));
  const next = ordered.find((window) => Date.parse(window.startsAt) > at);
  return { current: current ?? null, next: next ?? null };
}

/**
 * Whether two windows touch at all.
 *
 * Half open at both ends, so a performance ending at 10:15 and the next beginning at 10:15
 * do not overlap. This is the rule behind a festival's participant clash — a child cannot
 * be warming up in one room while performing in another.
 */
export function windowsOverlap(left: InstantWindow, right: InstantWindow): boolean {
  return Date.parse(left.startsAt) < Date.parse(right.endsAt)
    && Date.parse(right.startsAt) < Date.parse(left.endsAt);
}

/** Never negative, and whole, because everything above this counts in minutes. */
function wholeMinutes(milliseconds: number): number {
  return Math.max(0, Math.round(milliseconds / 60_000));
}

/**
 * A date and a wall-clock time in the convention's own zone, as an instant.
 *
 * The schedule is written the way a printed programme is written — "2026-09-17" and
 * "11:30" — which is not a moment until somebody says where. Read as UTC it would put the
 * opening session four hours early, and a countdown that is four hours wrong is worse
 * than no countdown.
 *
 * Guessed, then corrected, then corrected again. Two passes settle every case except a
 * time that falls inside a daylight-saving jump, and the convention is in September.
 */
export function instantOf(date: string, time: string, timeZone: string): string {
  const key = `${timeZone}|${date}|${time}`;
  const known = instants.get(key);
  if (known !== undefined) return known;
  const answer = solve(date, time, timeZone);
  // A convention is a dozen blocks over two days, so this holds a few dozen entries. The
  // bound is here for the case nobody has thought of rather than for any known one.
  if (instants.size < 4_096) instants.set(key, answer);
  return answer;
}

/**
 * The same date, time and zone give the same instant every time, and the schedule asks
 * for the same two dozen of them over and over: once per block, per screen, per attendee,
 * once a minute, for two days.
 *
 * Worth measuring rather than assuming. Working out where the convention has got to, for
 * a convention's worth of lock-screen cards, took 519ms before every part of the
 * application shared this clock and 1,504ms after, because each block's start and end
 * became a solve of its own. With this it is faster than either.
 */
const instants = new Map<string, string>();

function solve(date: string, time: string, timeZone: string): string {
  const target = Date.parse(`${date}T${time.slice(0, 5)}:00Z`);
  let guess = target;
  for (let pass = 0; pass < 2; pass += 1) {
    // What wall clock does this guess actually show in Beckley? The gap between that and
    // the wall clock we wanted is the offset, and it is measured against the target every
    // time rather than against the guess — measuring against the guess re-applies the
    // correction on the second pass and lands four hours the other side.
    const shown = Date.parse(`${wallClockIn(new Date(guess), timeZone)}:00Z`);
    if (shown === target) break;
    guess -= shown - target;
  }
  return new Date(guess).toISOString();
}

/**
 * A wall clock and its zone, as an instant. The inverse of `wallClockIn`.
 *
 * A person types "09:00" into a box and means nine o'clock where the thing happens, not
 * nine o'clock where they happen to be sitting. Doing that conversion on the server rather
 * than in a browser is the point: an organizer working from another state, and a second
 * client written later, cannot each land on a different answer.
 *
 * Takes "2026-04-11T09:00", the shape an HTML date-and-time field produces.
 */
export function instantOfWallClock(wallClock: string, timeZone: string): string {
  const [date = "", time = ""] = wallClock.trim().split("T");
  return instantOf(date, time, timeZone);
}

/**
 * Building a date formatter is expensive and this asks for the same one constantly: twice
 * per block per card, one card per attendee, once a minute, for two days. Built once per
 * zone and kept. Measured before it was added — two thousand eight hundred cards took
 * seven and a half seconds without this and a fraction of a second with it.
 *
 * The convention has one time zone, so the map holds one entry.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const existing = formatters.get(timeZone);
  if (existing) return existing;
  const built = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  formatters.set(timeZone, built);
  return built;
}

/**
 * What the clock on the wall says where the convention is, for a given instant, as
 * "2026-09-17T11:30". The date comes with it, which is what separates this from reading
 * an hour and a minute off a device.
 */
export function wallClockIn(instant: Date, timeZone: string): string {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}T${value("hour")}:${value("minute")}`;
}
